import { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import GrCard from "./GrCard";
import GrIcon from "./GrIcon";
import GrButton from "./GrButton";
import icons from "../icons";
import { gr } from "../tokens";

// Card with a cover image (16:9) on top: programmes, videos… Title and description are cut to
// two lines so cards in a grid stay the same height; `meta` is a small line under them and
// `actions` sit at the bottom. `badge` floats on the image. With `onClick` a click anywhere on the
// card opens the item, and the title is the keyboard / screen-reader button for it (the card itself
// is not a button, so the buttons inside are not nested in one); buttons in `actions` only do their
// own thing. No image → a soft placeholder icon.
// `actions` sit at the bottom. `badge` floats on the image. With `onClick` the whole card opens
// the item through one transparent button laid over the card (named by the title), and the buttons
// inside sit above it, so no button is nested in another (screen readers, axe, automated tests).
// No image → a soft placeholder icon. `eagerImage` loads the image at once: pass it for the cards in
// the first visible row, so the largest image on the page is not held back by lazy loading.
// `play` puts a round play mark in the middle of the image (video cards). The image is never cut:
// it fits whole in the 16:9 box over a blurred copy of itself (a 16:9 upload fills it exactly —
// admin shows that size next to the upload). A description longer than its two lines gets a link under
// it: with `expandDescription` it opens right there in the card ("View more" / "View less", the member
// stays on the list); with `onMoreDescription` "View more" runs that instead (e.g. opens the details).
const clamp = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

// Clickable card: pointer + hover border; the focus ring shows on the card when its title has focus.
const clickableSx = {
  cursor: "pointer",
  transition: "border-color .15s ease, box-shadow .15s ease",
  "&:hover": { borderColor: gr.color.line },
  "&:has(.gr-media-open:focus-visible)": {
    borderColor: gr.color.brand,
    boxShadow: gr.shadow.focus,
  },
};
const titleButtonSx = {
  display: "inline",
  p: 0,
  m: 0,
  border: 0,
  background: "none",
  font: "inherit",
  color: "inherit",
  textAlign: "inherit",
  cursor: "pointer",
  "&:focus-visible": { outline: "none" },
};

// A click on a button / link in `actions` is that button's own, not the card's.
const keepOwnClick = (e) => {
  if (e.target.closest("button, a, input, [role='button']"))
    e.stopPropagation();
};

export default function GrMediaCard({
  image,
  imageAlt = "",
  placeholderIcon = icons.image,
  badge,
  play = false,
  title,
  description,
  onMoreDescription,
  expandDescription = false,
  meta,
  actions,
  onClick,
  eagerImage = false,
  as = "article",
  sx,
}) {
  const titleId = useId();
  const descriptionRef = useRef(null);
  // An image that does not load shows the placeholder icon instead of a broken picture.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [image]);
  const showImage = Boolean(image) && !imageFailed;
  const [cut, setCut] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasMore = Boolean(onMoreDescription) || expandDescription;
  // An image that fails to load (missing file, wrong bucket) shows the placeholder icon instead of a
  // broken image with its alt text spilling over the card.
  useEffect(() => setImageFailed(false), [image]);
  // Is the description longer than its two lines? (re-checked when the card resizes)
  useEffect(() => {
    const el = descriptionRef.current;
    if (!el || !hasMore) return undefined;
    // Measured in the next frame: setting state inside the observer's own callback makes the browser
    // report a "ResizeObserver loop" when the text's height changes.
    let frame = 0;
    const check = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        setCut(el.scrollHeight - el.clientHeight > 1)
      );
    };
    check();
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null;
    if (observer) observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
    };
  }, [description, hasMore, expanded]);
  useEffect(() => setExpanded(false), [description]);

  return (
    <GrCard
      as={as}
      sx={{
        position: "relative",
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "@media (max-width: 599.95px)": { padding: 0 },
        ...(onClick
          ? {
              transition: "border-color .15s ease, box-shadow .15s ease",
              "&:hover": { borderColor: gr.color.line },
              "&:has(> .gr-media-open:focus-visible)": {
                borderColor: gr.color.brand,
                boxShadow: gr.shadow.focus,
              },
            }
          : {}),
        ...sx,
      }}
    >
      {onClick ? (
        <Box
          component="button"
          type="button"
          className="gr-media-open"
          aria-labelledby={titleId}
          onClick={onClick}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1, // over the image and text, so a click anywhere opens the item
            p: 0,
            border: 0,
            background: "transparent",
            borderRadius: "inherit",
            cursor: "pointer",
            "&:focus-visible": { outline: "none" },
          }}
        />
      ) : null}
      <Box
        sx={{
          position: "relative",
          aspectRatio: "16 / 9",
          overflow: "hidden",
          background: gr.color.tint,
          color: gr.color.brand,
          display: "grid",
          placeItems: "center",
          "& > svg": { width: gr.space[12], height: gr.space[12] },
        }}
      >
        {image && !imageFailed ? (
          <>
            {/* The whole image shows (tall posters too); a blurred copy fills the sides. */}
            <Box
              component="img"
              src={image}
              onError={() => setImageFailed(true)}
              alt=""
              aria-hidden="true"
              loading={eagerImage ? "eager" : "lazy"}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "blur(18px)",
                transform: "scale(1.2)",
                opacity: 0.55,
              }}
            />
            <Box
              component="img"
              src={image}
              alt={imageAlt}
              loading={eagerImage ? "eager" : "lazy"}
              fetchpriority={eagerImage ? "high" : undefined}
              onError={() => setImageFailed(true)}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </>
        ) : (
          <GrIcon icon={placeholderIcon} />
        )}
        {badge ? (
          <Box
            sx={{ position: "absolute", top: gr.space[3], left: gr.space[3] }}
          >
            {badge}
          </Box>
        ) : null}
        {play ? (
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: gr.space[12],
              height: gr.space[12],
              display: "grid",
              placeItems: "center",
              borderRadius: gr.radius.pill,
              background: gr.color.overlay,
              color: gr.color.onBrand,
              "& svg": { width: gr.space[8], height: gr.space[8] },
            }}
          >
            <GrIcon icon={icons.playArrow} />
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: gr.space[3],
          p: gr.space[5],
          pb: actions ? gr.space[4] : gr.space[5],
          minWidth: 0,
        }}
      >
        {/* Title and description read as one block (4px apart); the meta line sits 12px below it. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: gr.space[1],
            minWidth: 0,
          }}
        >
          <Box
            component="h3"
            id={titleId}
            sx={{
              m: 0,
              // !important only to beat the legacy global `h3 { margin-bottom: .5rem !important }`
              // (src/assets/css/style.css, table.css), which pushed the description away.
              mb: "0 !important",
              fontFamily: gr.font.family,
              fontSize: gr.font.size.lg,
              fontWeight: gr.font.weight.semibold,
              lineHeight: gr.font.lineHeight.tight,
              color: gr.color.ink,
              overflowWrap: "anywhere",
              ...clamp(2),
            }}
          >
            {onClick ? (
              <Box
                component="button"
                type="button"
                className="gr-media-open"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick(e);
                }}
                sx={titleButtonSx}
              >
                {title}
              </Box>
            ) : (
              title
            )}
          </Box>
          {description ? (
            <Box
              component="p"
              ref={descriptionRef}
              sx={{
                m: 0,
                fontSize: gr.font.size.sm,
                color: gr.color.ink2,
                // 20px lines keep the description on the 4px grid (13 × 1.5 was 19.5px).
                lineHeight: gr.space[5],
                overflowWrap: "anywhere",
                ...(expanded ? {} : clamp(2)),
              }}
            >
              {description}
            </Box>
          ) : null}
          {description && hasMore && (cut || expanded) ? (
            <GrButton
              variant="link"
              size="sm"
              aria-expanded={expandDescription ? expanded : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (expandDescription) setExpanded((open) => !open);
                else onMoreDescription();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                position: "relative",
                zIndex: 2,
                alignSelf: "flex-start",
              }}
            >
              {expanded ? "View less" : "View more"}
            </GrButton>
          ) : null}
        </Box>
        {meta ? (
          <Box
            sx={{
              mt: "auto",
              fontSize: gr.font.size.xs,
              color: gr.color.muted,
            }}
          >
            {meta}
          </Box>
        ) : null}
      </Box>
      {actions ? (
        <Box
          onClick={keepOwnClick}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: gr.space[2],
            px: gr.space[5],
            pb: gr.space[5],
            // Above the card's open button, so its own buttons get their clicks.
            position: "relative",
            zIndex: 2,
          }}
        >
          {actions}
        </Box>
      ) : null}
    </GrCard>
  );
}

GrMediaCard.propTypes = {
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  placeholderIcon: PropTypes.object,
  badge: PropTypes.node,
  play: PropTypes.bool,
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  onMoreDescription: PropTypes.func,
  expandDescription: PropTypes.bool,
  meta: PropTypes.node,
  actions: PropTypes.node,
  onClick: PropTypes.func,
  eagerImage: PropTypes.bool,
  as: PropTypes.oneOf(["section", "article", "div", "li"]),
  sx: PropTypes.object,
};
